import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExchangeProduct,
  LineItem,
  LocationState,
  ReturnFullProduct,
  ReturnProduct,
} from '../utils/types';
import {
  buildLineItemDenyRules,
  buildPhotoRules,
  photoRuleReferencesReason as referencesReason,
  formatPrice,
  getFeToken,
  registerBetweenOperator,
  shopLabel,
} from '../utils/helpers';
import { discountedUnitPriceCap } from '../utils/returnPricing';
import {
  registerListOperators,
  registerTextOperators,
} from '../utils/ruleOperators';
import { computeReturnable } from '../utils/returnable';
import { clearMethodsDrafts } from '../utils/methodsDraft';
import {
  codePointLength,
  detailsSettingsOf,
  isDetailsRequired,
  isDetailsShown,
  limitCodePoints,
  PortalDetailsMode,
} from '../utils/portalDetails';
import { useTranslation } from 'react-i18next';
import { reasonLabel } from '../i18n/reasonLabel';
import { Engine, Event } from 'json-rules-engine';
import Stepper from './Stepper';
import ActionBar from './ActionBar';
import ImagePicker from './ImagePicker';
import ShopifyProductsModal from './ShopifyProductModal';
import { useStandaloneQr } from '../contexts/StandaloneQrContext';
import { standaloneNavigationState } from '../utils/qrEntry';

type ActionType = 'return' | 'exchange';

// #496: the "Detalii" field of a line: by the shop's mode, and always (and
// required) on a line the server refused for a missing text (review r1 #1:
// the server's photo matching wins over the portal's approximation)
const detailsShownFor = (
  mode: PortalDetailsMode,
  line: Pick<ReturnProduct, 'requirePhoto' | 'serverRequiresDetails'>
): boolean =>
  isDetailsShown(mode, line.requirePhoto) ||
  line.serverRequiresDetails === true;
const detailsRequiredFor = (
  mode: PortalDetailsMode,
  line: Pick<ReturnProduct, 'requirePhoto' | 'serverRequiresDetails'>
): boolean =>
  isDetailsRequired(mode, line.requirePhoto) ||
  line.serverRequiresDetails === true;

const OrderDetailsPage: React.FC<LocationState> = ({
  order_token,
  requirements,
  offer_correlation_id,
  offer_session,
  products_to_return,
  return_methods,
  return_reasons,
  payment_methods,
  proof,
  shop,
  allow_exchanges,
  allow_exchange_more_expensive,
  terms_and_conditions_url,
  withdrawal_id,
  withdrawal_receipt,
  portal_details_mode,
  portal_details_max_length,
  details_error,
  methods_draft_id,
}) => {
  const { t, i18n } = useTranslation(['translation', 'dynamic']);
  const navigate = useNavigate();
  // #72: a full-screen QR entry has no lookup form to go back to
  const { active: standaloneQr, handoff, account } = useStandaloneQr();

  const order = order_token.order_details;
  const feActions = requirements.fe_actions;
  // #149: a case C return of a withdrawal declaration. No reason is asked
  // (return-api sets the shop's system reason), the DENY_RETURN grey-out does
  // not apply (the server skips every denial on this entry) and a rule that
  // depends on the reason is unknown here: skipped, the server judges it.
  const withdrawalMode = withdrawal_id !== undefined;
  // #496: the shop's "Detalii" setting (absent: SHOWN / 500, as before)
  const { mode: detailsMode, maxLength: detailsMaxLength } = detailsSettingsOf({
    portal_details_mode,
    portal_details_max_length,
  });
  const [showUploadModal, setShowUploadModal] = useState<{
    id: number;
    index: number;
  } | null>(null);

  const [validationErrors, setValidationErrors] = useState<Set<string>>(
    new Set()
  );

  const [showExchangeModal, setShowExchangeModal] = useState<{
    id: number;
    index: number;
  } | null>(null);
  const [feToken, setFeToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    const loadToken = async () => {
      try {
        const token = await getFeToken();
        setFeToken(token);
      } catch (err) {
        console.error('Could not load fe token', err);
      }
    };
    loadToken();
  }, []);

  const handleProductSelect = (
    productExchange: ExchangeProduct | null,
    lineItemId?: number,
    index?: number
  ) => {
    setSelectedToReturn(prevProducts => {
      let matchIndex = -1;
      return prevProducts.map(product => {
        if (product.id === lineItemId) {
          matchIndex++;
          if (matchIndex === index) {
            return {
              ...product,
              exchangeProduct: productExchange,
            };
          }
          return product;
        }
        return product;
      });
    });
  };

  // #496: a 400 of POST /returns on a line's text comes back with this step
  // (line_index = the index in products_to_return, the order submitted); it
  // is attached to that line, so it follows the line through a split /
  // merge (review r1 #4). A text the current mode does not show (e.g. kept
  // from a looser mode) is dropped on entry.
  const [selectedToReturn, setSelectedToReturn] = useState<ReturnProduct[]>(
    () =>
      products_to_return.map((product, index) => {
        // detailsError: the message shown on arrival (not carried on);
        // serverRequiresDetails: the persistent requirement (review r3)
        const errorHere = details_error?.line_index === index;
        const line: ReturnProduct = {
          ...product,
          detailsError: errorHere ? details_error.code : undefined,
          serverRequiresDetails:
            product.serverRequiresDetails === true ||
            (errorHere && details_error.code === 'DESCRIPTION_REQUIRED') ||
            undefined,
        };
        return {
          ...line,
          details: detailsShownFor(detailsMode, line) ? line.details : '',
        };
      })
  );
  // #496 (review r1 #3): the value of a field when its IME composition
  // started, by row; no limit applies until the composition ends
  const composingFrom = useRef(new Map<string, string>());

  // Create stable references for complex objects
  const allProducts = useMemo(() => {
    const map = new Map<number, LineItem>();
    order.line_items.forEach(function (item) {
      map.set(item.id, item);
    });
    return map;
  }, [order.line_items]);

  const returnProductsFull = useMemo((): ReturnFullProduct[] => {
    return selectedToReturn
      .map(prod => {
        const p = allProducts.get(prod.id);
        if (!p) return null;
        return {
          ...p,
          reason: prod.reason,
          quantityReturn: prod.quantity,
          // #496: a hidden field never carries text to the submission
          details: detailsShownFor(detailsMode, prod) ? prod.details : '',
          requirePhoto: prod.requirePhoto,
          // #496 (review r3): travels to the preferences step and back
          serverRequiresDetails: prod.serverRequiresDetails,
          photos: [...prod.photos],
          showIndividual: prod.showIndividual,
          exchangeProduct: prod.exchangeProduct,
        };
      })
      .filter(Boolean) as ReturnFullProduct[];
  }, [selectedToReturn, allProducts, detailsMode]);

  const validateProducts = useCallback((): boolean => {
    const errors = new Set<string>();
    // per line item occurrence, the same index the picker target uses
    const occurrences = new Map<number, number>();

    selectedToReturn.forEach((product, idx) => {
      const productKey = `${product.id}-${idx}`;
      const occurrence = occurrences.get(product.id) ?? 0;
      occurrences.set(product.id, occurrence + 1);

      // #78: the exchange picker is still open on this row (no product
      // chosen yet, nor switched back to Return)
      if (
        showExchangeModal?.id === product.id &&
        showExchangeModal?.index === occurrence
      ) {
        errors.add(`${productKey}-exchange`);
      }

      // Check if reason is selected (#149: none in withdrawal mode)
      if (!withdrawalMode && (!product.reason || product.reason === '')) {
        errors.add(`${productKey}-reason`);
      }

      // #496: the text is required only on a photo line in SHOWN, and a
      // shown text stays within the shop's limit (code points)
      if (
        detailsRequiredFor(detailsMode, product) &&
        (!product.details || product.details.trim() === '')
      ) {
        errors.add(`${productKey}-details`);
      }
      if (
        detailsShownFor(detailsMode, product) &&
        codePointLength(product.details ?? '') > detailsMaxLength
      ) {
        errors.add(`${productKey}-details-long`);
      }
      // Check if photo is required and missing
      if (product.requirePhoto) {
        if (!product.photos || product.photos.length === 0) {
          errors.add(`${productKey}-photos`);
        }
      }
    });

    setValidationErrors(errors);
    return errors.size === 0;
  }, [
    selectedToReturn,
    showExchangeModal,
    withdrawalMode,
    detailsMode,
    detailsMaxLength,
  ]);

  const handleContinueClick = useCallback(async () => {
    if (!validateProducts()) {
      // Scroll to first error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    navigate('/methods', {
      state: {
        order_token: order_token,
        return_products: returnProductsFull,
        return_methods: return_methods,
        payment_methods: payment_methods,
        requirements: requirements,
        offer_correlation_id,
        offer_session,
        return_reasons: return_reasons,
        proof: proof,
        shop: shop,
        allow_exchanges: allow_exchanges,
        allow_exchange_more_expensive: allow_exchange_more_expensive,
        terms_and_conditions_url: terms_and_conditions_url,
        portal_details_mode: detailsMode,
        portal_details_max_length: detailsMaxLength,
        // #496 (review r1 #2): the preferences step's choices come back
        ...(methods_draft_id ? { methods_draft_id } : {}),
        ...(withdrawalMode ? { withdrawal_id, withdrawal_receipt } : {}),
        ...(standaloneQr ? standaloneNavigationState(handoff, account) : {}),
      },
    });
  }, [
    withdrawalMode,
    withdrawal_id,
    withdrawal_receipt,
    standaloneQr,
    handoff,
    validateProducts,
    navigate,
    order_token,
    returnProductsFull,
    return_methods,
    payment_methods,
    requirements,
    offer_correlation_id,
    offer_session,
    return_reasons,
    proof,
    shop,
    terms_and_conditions_url,
    detailsMode,
    detailsMaxLength,
    methods_draft_id,
  ]);

  // Grey-out engine (#87): one rule per line_item DENY_RETURN action, its
  // conditions ANDed, rules ORed. Order/return-level conditions inside such a
  // rule are ignored except order.payment_method (known limitation, see README).
  const engine = useMemo(() => {
    const ruleEngine = new Engine([], { allowUndefinedFacts: true });
    registerBetweenOperator(ruleEngine);
    registerListOperators(ruleEngine);
    registerTextOperators(ruleEngine);
    // #149: no grey-out on a withdrawal return
    buildLineItemDenyRules(withdrawalMode ? [] : feActions).forEach(rule =>
      ruleEngine.addRule(rule)
    );
    return ruleEngine;
  }, [feActions, withdrawalMode]);

  const [engineReturnProducts] = useState(() => {
    const ruleEngine = new Engine([], { allowUndefinedFacts: true });
    registerBetweenOperator(ruleEngine);
    registerListOperators(ruleEngine);
    registerTextOperators(ruleEngine);
    buildPhotoRules(feActions)
      // #149: the reason is unknown in withdrawal mode: a rule on it is
      // skipped (never evaluated against an empty reason)
      .filter(rule => !withdrawalMode || !referencesReason(rule))
      .forEach(rule => ruleEngine.addRule(rule));

    return ruleEngine;
  });

  const evaluateRules = useCallback(
    async (prods: ReturnProduct[]) => {
      const updates: { id: number; index: number; requirePhoto: boolean }[] =
        [];

      // Group products by id to track index within each group
      const productsByIdIndex = new Map<string, number>();

      for (let i = 0; i < prods.length; i++) {
        const product = prods[i];
        const key = `${product.id}`;
        const indexForId = productsByIdIndex.get(key) || 0;
        productsByIdIndex.set(key, indexForId + 1);

        let newVisibility: boolean = false;

        try {
          if (withdrawalMode) {
            // #149: only the reason-free rules (quantity) are known here
            const results = await engineReturnProducts.run({
              'order.payment_method': order.payment_method,
              quantity: product.quantity,
            });
            updates.push({
              id: product.id,
              index: indexForId,
              requirePhoto: results.events.some(
                (event: Event) => event.type === 'showField'
              ),
            });
          } else if (product.reason && product.reason !== '') {
            const reasonName = return_reasons.filter(
              c => c.value === Number(product.reason)
            )[0].name;
            const results = await engineReturnProducts.run({
              'order.payment_method': order.payment_method,
              reason: reasonName,
              quantity: product.quantity,
            });

            results.events.forEach((event: Event) => {
              if (event.type === 'showField') {
                newVisibility = true;
              }
            });

            updates.push({
              id: product.id,
              index: indexForId,
              requirePhoto: newVisibility,
            });
          }
        } catch (error) {
          console.error('Rules evaluation error:', error);
        }
      }

      if (updates.length > 0) {
        setSelectedToReturn(prevProducts => {
          const matchCounts = new Map<number, number>();

          return prevProducts.map(p => {
            const currentMatchIndex = matchCounts.get(p.id) || 0;
            matchCounts.set(p.id, currentMatchIndex + 1);

            const update = updates.find(
              u => u.id === p.id && u.index === currentMatchIndex
            );
            // #496: a field the new photo state hides (reason change,
            // split / merge) drops its text
            if (!update) return p;
            const line = { ...p, requirePhoto: update.requirePhoto };
            return {
              ...line,
              details: detailsShownFor(detailsMode, line) ? line.details : '',
            };
          });
        });
      }
    },
    [
      engineReturnProducts,
      return_reasons,
      withdrawalMode,
      order.payment_method,
      detailsMode,
    ]
  );

  useEffect(() => {
    if (selectedToReturn.length > 0) {
      evaluateRules(selectedToReturn);
    }
  }, [
    selectedToReturn.map(p => `${p.id}-${p.reason}-${p.quantity}`).join(','),
    evaluateRules,
  ]);

  // #107 (r1-8): focus management after Add / Remove, applied once the
  // re-rendered card carries the target button
  const [focusTarget, setFocusTarget] = useState<{
    id: number;
    kind: 'add' | 'remove';
  } | null>(null);
  useEffect(() => {
    if (!focusTarget) return;
    document
      .getElementById(`line-${focusTarget.id}-${focusTarget.kind}`)
      ?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const [greyedOutItems, setGreyedOutItems] = useState<Set<number>>(new Set());

  const parseTags = (tags: string | string[]): string[] => {
    if (Array.isArray(tags)) return tags;
    try {
      return JSON.parse(tags);
    } catch {
      return tags.split(',').map(t => t.trim());
    }
  };

  useEffect(() => {
    const checkItems = async () => {
      const greyedSet = new Set<number>();

      for (const item of order.line_items) {
        try {
          const tagsArray = parseTags(item.tags ?? '');
          const ruleData = {
            ...item,
            tags: tagsArray,
            'order.payment_method': order.payment_method,
          };
          const { events } = await engine.run(ruleData);
          if (events.some((event: Event) => event.type === 'grey-out-item')) {
            greyedSet.add(item.id);
          }
        } catch (error) {
          console.error('Error running rule engine for item:', item.id, error);
        }
      }

      setGreyedOutItems(greyedSet);
    };

    if (order.line_items.length > 0 && engine) {
      checkItems().catch(error => {
        console.error('Error checking items:', error);
      });
    }
  }, [order.line_items, order.payment_method, engine]);

  const toggleProductSelection = (id: number) => {
    const product = allProducts.get(id);
    if (product !== undefined) {
      const toReturnById = selectedToReturn.filter(p => p.id === id).length;
      if (toReturnById === 0) {
        const item = order.line_items.filter(i => i.id === id)[0];
        // #144: start at the fulfilled (returnable) ceiling, not the full ordered quantity, so a
        // partially-fulfilled line never pre-fills above what Shopify can actually return.
        const { maxReturnable } = computeReturnable(
          item.quantity,
          item.fulfilled_quantity,
          item.returnable_quantity
        );
        // add product to return
        const newProduct: ReturnProduct = {
          id: id,
          reason: '', // Start with empty reason
          quantity: maxReturnable,
          details: '',
          photos: [],
          requirePhoto: false,
          showIndividual: false,
          exchangeProduct: null,
        };
        setSelectedToReturn(prevProducts => [...prevProducts, newProduct]);
        // #107 (r1-8): the focus goes to the card's Remove button
        setFocusTarget({ id, kind: 'remove' });
      } else {
        // remove product to return
        setSelectedToReturn(prevProducts =>
          prevProducts.filter(product => product.id !== id)
        );
        // #78: its rows (and indexes) are gone; drop a picker target on them
        setShowExchangeModal(prev => (prev?.id === id ? null : prev));
        // #107 (r1-8): the focus goes back to the card's Add button
        setFocusTarget({ id, kind: 'add' });
      }
    }
  };

  const isProductSelected = (id: number) =>
    selectedToReturn.filter(p => p.id === id).length > 0;

  const productSelected = (id: number): ReturnProduct =>
    selectedToReturn.filter(p => p.id === id)[0];

  const updateQuantity = (id: number, delta: number) => {
    // #144: enforce the fulfilled ceiling here too, not only via the disabled +
    // button — rapid repeated clicks could fire before the re-render disables it.
    const lineItem = order.line_items.find(i => i.id === id);
    if (!lineItem) {
      return; // unknown line item — nothing to update (never falls back to unbounded)
    }
    const { maxReturnable } = computeReturnable(
      lineItem.quantity,
      lineItem.fulfilled_quantity,
      lineItem.returnable_quantity
    );
    const showIndividual = selectedToReturn.filter(s => s.id === id)[0]
      .showIndividual;
    if (!showIndividual) {
      setSelectedToReturn(prevProducts =>
        prevProducts.map(product =>
          product.id === id
            ? {
                ...product,
                quantity: Math.min(
                  maxReturnable,
                  Math.max(1, product.quantity + delta)
                ),
                // #496 (review r2 #2): a new quantity, new rule inputs
                detailsError: undefined,
                serverRequiresDetails: undefined,
              }
            : product
        )
      );
    } else {
      if (delta === 1) {
        const newProduct: ReturnProduct = {
          id: id,
          reason: '', // Start with empty reason
          quantity: 1,
          details: '',
          photos: [],
          requirePhoto: false,
          showIndividual: showIndividual,
          exchangeProduct: null,
        };
        // Don't add an individual return entry beyond the fulfilled ceiling.
        setSelectedToReturn(prevProducts =>
          prevProducts.filter(p => p.id === id).length >= maxReturnable
            ? prevProducts
            : [...prevProducts, newProduct]
        );
      } else {
        setSelectedToReturn(prevProducts => {
          const lastIndex = prevProducts.map(p => p.id).lastIndexOf(id);
          if (lastIndex !== -1) {
            return prevProducts.filter((_, index) => index !== lastIndex);
          }
          return prevProducts;
        });
      }
    }
  };

  const updateReason = useCallback(
    (id: number, index: number, reason: string) => {
      setSelectedToReturn(prevProducts => {
        let matchIndex = -1;
        const updated = prevProducts.map(product => {
          if (product.id === id) {
            matchIndex++;
            if (matchIndex === index) {
              // #496 (review r2 #2): the server's refusal applied to the
              // previous reason: the forced requirement goes with it
              return {
                ...product,
                reason: reason,
                detailsError: undefined,
                serverRequiresDetails: undefined,
              };
            }
            return product;
          }
          return product;
        });
        return updated;
      });
    },
    []
  );

  const updateDetails = useCallback(
    (id: number, index: number, details: string) => {
      setSelectedToReturn(prevProducts => {
        let matchIndex = -1;
        const updated = prevProducts.map(product => {
          if (product.id === id) {
            matchIndex++;
            if (matchIndex === index) {
              return {
                ...product,
                details: details,
              };
            }
            return product;
          }
          return product;
        });
        return updated;
      });
    },
    []
  );

  const handleImagesSelected = useCallback(
    (id: number, index: number, files: File[]) => {
      setSelectedToReturn(prevProducts => {
        let matchIndex = -1;
        const updated = prevProducts.map(product => {
          if (product.id === id) {
            matchIndex++;
            if (matchIndex === index) {
              return {
                ...product,
                photos: files,
              };
            }
            return product;
          }
          return product;
        });
        return updated;
      });
    },
    []
  );

  const handleRemoveImage = useCallback(
    (id: number, index: number, indexPhoto: number) => {
      setSelectedToReturn(prevProducts => {
        let matchIndex = -1;
        const updated = prevProducts.map(product => {
          if (product.id === id) {
            matchIndex++;
            if (matchIndex === index) {
              const updatedImages = product.photos.filter(
                (_, i) => i !== indexPhoto
              );
              return {
                ...product,
                photos: updatedImages,
              };
            }
            return product;
          }
          return product;
        });
        return updated;
      });
    },
    []
  );

  const updateShowIndividual = (id: number) => {
    setSelectedToReturn(prevProducts => {
      const updatedProducts = prevProducts.map(product =>
        product.id === id
          ? {
              ...product,
              showIndividual: !product.showIndividual,
              // #496 (review r2 #2): a split / merge changes the rule inputs
              detailsError: undefined,
              serverRequiresDetails: undefined,
            }
          : product
      );

      const updatedProduct = updatedProducts.find(p => p.id === id);
      const isNowShowIndividual = updatedProduct?.showIndividual;

      if (!isNowShowIndividual) {
        const groupedMap = new Map<number, ReturnProduct>();
        updatedProducts.forEach(product => {
          const key = product.id;
          if (groupedMap.has(key)) {
            const existing = groupedMap.get(key)!;
            existing.quantity += product.quantity;
          } else {
            groupedMap.set(key, { ...product });
          }
        });
        return Array.from(groupedMap.values());
      } else {
        const normalized: ReturnProduct[] = [];
        updatedProducts.forEach(product => {
          if (product.id === id) {
            for (let i = 0; i < product.quantity; i++) {
              normalized.push({
                ...product,
                quantity: 1,
              });
            }
          } else {
            normalized.push(product);
          }
        });
        return normalized;
      }
    });
  };

  const isContinueDisabled = useMemo(() => {
    // If no products selected
    return selectedToReturn.length === 0;
  }, [selectedToReturn]);

  const renderToReturnItem = useCallback(
    (
      index: number,
      total: number,
      item: LineItem,
      selectedQuantity: number
    ) => {
      const productListForId = selectedToReturn.filter(p => p.id === item.id);
      const selectedProduct = productListForId[index];
      const previews = new Map<string, string>();
      selectedProduct?.photos.forEach(function (item) {
        previews.set(item.name, URL.createObjectURL(item));
      });
      // #107: per-occurrence ids (r1-7) and the "Item n" heading (total > 1)
      const rowId = `${item.id}-${index}`;
      const itemHeading =
        total > 1 ? String(t('products.item', { n: index + 1 })) : '';
      const isModalOpen =
        showUploadModal?.id === item.id && showUploadModal?.index === index;
      // #78: a row is an exchange iff it carries an exchange product; the
      // only pending state is an open picker (blocks Continue)
      const action: ActionType = selectedProduct?.exchangeProduct
        ? 'exchange'
        : 'return';
      const isPickerOpen =
        showExchangeModal?.id === item.id && showExchangeModal?.index === index;
      const onExchange = action === 'exchange' || isPickerOpen;
      const reasonText = reasonLabel(i18n, action);

      // Calculate the actual index in selectedToReturn array
      const actualIndex = selectedToReturn.findIndex((p, i) => {
        let count = 0;
        for (let j = 0; j <= i; j++) {
          if (selectedToReturn[j].id === item.id) {
            if (count === index) return j === i;
            count++;
          }
        }
        return false;
      });

      const productKey = `${item.id}-${actualIndex}`;
      // #496: the field by the shop's mode; its counter in code points; a
      // server error on this line shows until the text is corrected
      const details = selectedProduct?.details ?? '';
      const detailsLine = {
        requirePhoto: selectedProduct?.requirePhoto ?? false,
        detailsError: selectedProduct?.detailsError,
        serverRequiresDetails: selectedProduct?.serverRequiresDetails,
      };
      const showDetails = detailsShownFor(detailsMode, detailsLine);
      const hasDetailsError =
        validationErrors.has(`${productKey}-details`) ||
        (detailsLine.detailsError === 'DESCRIPTION_REQUIRED' &&
          details.trim() === '');
      const hasDetailsLongError =
        validationErrors.has(`${productKey}-details-long`) ||
        (detailsLine.detailsError === 'DESCRIPTION_TOO_LONG' &&
          codePointLength(details) > detailsMaxLength);
      const composingKey = rowId;
      const applyDetails = (value: string) => {
        updateDetails(item.id, index, value);
        setValidationErrors(prev => {
          const next = new Set(prev);
          if (value.trim()) next.delete(`${productKey}-details`);
          if (codePointLength(value) <= detailsMaxLength)
            next.delete(`${productKey}-details-long`);
          return next;
        });
      };
      const detailsDescribedBy =
        [
          `details-${rowId}-count`,
          hasDetailsError ? `details-${rowId}-error` : '',
          hasDetailsLongError ? `details-${rowId}-long-error` : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined;
      const hasPhotosError = validationErrors.has(`${productKey}-photos`);
      const hasReasonError = validationErrors.has(`${productKey}-reason`);
      // meaningful only while this row's picker is still the open one
      const hasExchangeError =
        isPickerOpen && validationErrors.has(`${productKey}-exchange`);
      const clearExchangeError = () =>
        setValidationErrors(prev => {
          if (!prev.has(`${productKey}-exchange`)) return prev;
          const next = new Set(prev);
          next.delete(`${productKey}-exchange`);
          return next;
        });
      const exchangeProduct = selectedProduct?.exchangeProduct ?? null;

      return (
        <React.Fragment key={rowId}>
          <div className="xc-divider" />
          <div className="xc-prod__details">
            {total > 1 && <span className="xc-prod__item">{itemHeading}</span>}
            {/* #149: no reason on a withdrawal return */}
            {!withdrawalMode && (
              <div className="xc-field">
                <label htmlFor={`reason-${rowId}`} className="xc-field__label">
                  {reasonText} <span className="xc-field__req">*</span>
                </label>
                <div className="xc-select">
                  <select
                    id={`reason-${rowId}`}
                    className="xc-select__control"
                    value={selectedProduct?.reason || ''}
                    aria-label={
                      total > 1 ? `${itemHeading} - ${reasonText}` : undefined
                    }
                    aria-invalid={hasReasonError || undefined}
                    aria-describedby={
                      hasReasonError ? `reason-${rowId}-error` : undefined
                    }
                    onChange={e => {
                      updateReason(item.id, index, e.target.value);
                      if (e.target.value) {
                        setValidationErrors(prev => {
                          const next = new Set(prev);
                          next.delete(`${productKey}-reason`);
                          return next;
                        });
                      }
                    }}
                  >
                    <option value="" disabled>
                      {t('productDetails.selectReason') || 'Select a reason'}
                    </option>
                    {return_reasons.map(option => (
                      <option key={option.value} value={option.value}>
                        {shopLabel(
                          'dynamic:return_reason_' +
                            option.value +
                            '.display_name',
                          option.label
                        )}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="xc-select__caret"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>
                {hasReasonError && (
                  <p
                    id={`reason-${rowId}-error`}
                    className="xc-field__error"
                    role="alert"
                  >
                    {t('validation.reasonRequired') || 'Please select a reason'}
                  </p>
                )}
              </div>
            )}
            {allow_exchanges && (
              <div className="xc-field">
                <span className="xc-field__label">{t('products.want')}</span>
                <div
                  className="xc-seg"
                  data-on={onExchange ? 'exchange' : 'return'}
                  aria-describedby={`want-${rowId}-hint`}
                  role="group"
                  aria-label={String(t('products.want'))}
                >
                  <span className="xc-seg__thumb" aria-hidden="true" />
                  <button
                    type="button"
                    className="xc-seg__btn"
                    aria-pressed={!onExchange}
                    onClick={() => {
                      handleProductSelect(null, item.id, index);
                      clearExchangeError();
                      // #78: the inline picker has no backdrop; close it
                      // if it is open for this row (not another row's)
                      if (isPickerOpen) setShowExchangeModal(null);
                    }}
                  >
                    {t('productDetails.return')}
                  </button>
                  <button
                    type="button"
                    className="xc-seg__btn"
                    aria-pressed={onExchange}
                    aria-expanded={isPickerOpen}
                    aria-controls={
                      isPickerOpen ? `exchange-picker-${rowId}` : undefined
                    }
                    aria-describedby={
                      hasExchangeError ? `exchange-${rowId}-error` : undefined
                    }
                    onClick={() => {
                      setShowExchangeModal({ id: item.id, index: index });
                    }}
                  >
                    {t('productDetails.exchange')}
                  </button>
                </div>
                <p id={`want-${rowId}-hint`} className="xc-seg__hint">
                  {onExchange
                    ? t('products.wantHint.exchange')
                    : t('products.wantHint.return')}
                </p>
                {hasExchangeError && (
                  <p
                    id={`exchange-${rowId}-error`}
                    className="xc-field__error"
                    role="alert"
                  >
                    {t('validation.exchangeProductRequired')}
                  </p>
                )}
                {isPickerOpen && (
                  <ShopifyProductsModal
                    id={`exchange-picker-${rowId}`}
                    show={true}
                    onHide={() => {
                      clearExchangeError();
                      setShowExchangeModal(null);
                    }}
                    onSelectProduct={product => {
                      handleProductSelect(product, item.id, index);
                      clearExchangeError();
                      setShowExchangeModal(null);
                    }}
                    shopDomain={shop}
                    useTokenlessAccess={false}
                    storefrontAccessToken={feToken}
                    minQuantity={selectedQuantity}
                    maxPrice={
                      allow_exchange_more_expensive
                        ? undefined
                        : discountedUnitPriceCap(item)
                    }
                    productTitle={item.product_title}
                    variantTitle={item.variant_title}
                  />
                )}
              </div>
            )}
            {/* D11 (r1-14): the exchange row sits right under the control */}
            {exchangeProduct !== null && (
              <div className="xc-prod xc-prod--exchange">
                {exchangeProduct.image_url ? (
                  <div className="xc-prod__img">
                    <img
                      src={exchangeProduct.image_url}
                      alt={exchangeProduct.product_title}
                    />
                  </div>
                ) : (
                  <div className="xc-prod__img xc-prod__img--empty">
                    <span className="xc-visually-hidden">
                      {t('products.noImage')}
                    </span>
                  </div>
                )}
                <div>
                  <p className="xc-prod__meta">
                    {t('productDetails.exchangeWith')}
                  </p>
                  <p className="xc-prod__name">
                    {exchangeProduct.product_title}
                    {exchangeProduct.variant_title !== 'Default Title' && (
                      <span className="xc-prod__variant">
                        {' '}
                        - {exchangeProduct.variant_title}
                      </span>
                    )}
                  </p>
                  <p className="xc-prod__meta">
                    <span className="xc-prod__price">
                      {formatPrice(
                        exchangeProduct.price,
                        exchangeProduct.currency
                      )}
                    </span>{' '}
                    {t('products.each')} {'·'}{' '}
                    <button
                      type="button"
                      className="xc-btn xc-btn--link"
                      onClick={() =>
                        setShowExchangeModal({ id: item.id, index: index })
                      }
                    >
                      {t('products.change')}
                    </button>
                  </p>
                </div>
              </div>
            )}
            {showDetails && (
              <div className="xc-field">
                <label htmlFor={`details-${rowId}`} className="xc-field__label">
                  {t('productDetails.extraInfo')}
                  {detailsRequiredFor(detailsMode, detailsLine) && (
                    <>
                      {' '}
                      <span className="xc-field__req">*</span>
                    </>
                  )}
                </label>
                <textarea
                  id={`details-${rowId}`}
                  className="xc-input xc-input--area"
                  rows={3}
                  placeholder={
                    action === 'return'
                      ? shopLabel(
                          'dynamic:app_message.enter_details_return',
                          ''
                        )
                      : shopLabel(
                          'dynamic:app_message.enter_details_exchange',
                          ''
                        )
                  }
                  value={details}
                  aria-invalid={
                    hasDetailsError || hasDetailsLongError || undefined
                  }
                  aria-describedby={detailsDescribedBy}
                  onCompositionStart={() => {
                    composingFrom.current.set(composingKey, details);
                  }}
                  onCompositionEnd={e => {
                    // review r1 #3: the committed text gets the limit
                    const from =
                      composingFrom.current.get(composingKey) ?? details;
                    composingFrom.current.delete(composingKey);
                    applyDetails(
                      limitCodePoints(
                        from,
                        e.currentTarget.value,
                        detailsMaxLength
                      )
                    );
                  }}
                  onChange={e => {
                    // #496: the limit in code points, not the native
                    // maxLength (UTF-16 units); never on an uncommitted IME
                    // composition (review r1 #3)
                    applyDetails(
                      composingFrom.current.has(composingKey)
                        ? e.target.value
                        : limitCodePoints(
                            details,
                            e.target.value,
                            detailsMaxLength
                          )
                    );
                  }}
                />
                <p
                  id={`details-${rowId}-count`}
                  className={
                    codePointLength(details) > detailsMaxLength
                      ? 'xc-field__count xc-field__count--over'
                      : 'xc-field__count'
                  }
                >
                  {`${codePointLength(details)}/${detailsMaxLength}`}
                </p>
                {hasDetailsError && (
                  <p
                    id={`details-${rowId}-error`}
                    className="xc-field__error"
                    role="alert"
                  >
                    {t('validation.detailsRequired') ||
                      'You need to enter some details about the damage'}
                  </p>
                )}
                {hasDetailsLongError && (
                  <p
                    id={`details-${rowId}-long-error`}
                    className="xc-field__error"
                    role="alert"
                  >
                    {t('validation.detailsTooLong', { max: detailsMaxLength })}
                  </p>
                )}
              </div>
            )}
            {/* #149: in withdrawal mode the picker is offered on every row
                (optional unless a known rule requires a photo), so a
                photo refusal at POST /returns can be corrected */}
            {(selectedProduct?.requirePhoto || withdrawalMode) && (
              <div className="xc-field">
                <div className="xc-field__head">
                  <span
                    id={`photos-${rowId}-label`}
                    className="xc-field__label"
                  >
                    {selectedProduct?.requirePhoto ? (
                      <>
                        {t('productDetails.image')}{' '}
                        <span className="xc-field__req">*</span>
                      </>
                    ) : (
                      t('withdrawal.photosOptional')
                    )}
                  </span>
                  <button
                    type="button"
                    className="xc-btn xc-btn--sm"
                    aria-describedby={
                      hasPhotosError ? `photos-${rowId}-error` : undefined
                    }
                    onClick={() =>
                      setShowUploadModal({ id: item.id, index: index })
                    }
                  >
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
                    </svg>
                    {t('imagePicker.upload')}
                  </button>
                </div>
                {hasPhotosError && (
                  <p
                    id={`photos-${rowId}-error`}
                    className="xc-field__error"
                    role="alert"
                  >
                    {t('validation.photoRequired') ||
                      'At least one picture is required'}
                  </p>
                )}
                <ImagePicker
                  show={isModalOpen}
                  onClose={() => setShowUploadModal(null)}
                  onAttach={files => {
                    handleImagesSelected(item.id, index, files);
                    if (files.length > 0) {
                      setValidationErrors(prev => {
                        const next = new Set(prev);
                        next.delete(`${productKey}-photos`);
                        return next;
                      });
                    }
                  }}
                  maxFiles={5}
                  maxSizeMB={5}
                />
                {selectedProduct.photos.length > 0 && (
                  <ul
                    className="xc-photos"
                    aria-labelledby={`photos-${rowId}-label`}
                  >
                    {selectedProduct.photos.map((img, indexPhoto) => (
                      <li key={indexPhoto} className="xc-photo">
                        <img
                          src={previews.get(img.name)}
                          alt={String(
                            t('products.photo.alt', { n: indexPhoto + 1 })
                          )}
                        />
                        <button
                          type="button"
                          className="xc-photo__remove"
                          aria-label={String(
                            t('products.photo.remove', { n: indexPhoto + 1 })
                          )}
                          onClick={() =>
                            handleRemoveImage(item.id, index, indexPhoto)
                          }
                        >
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </React.Fragment>
      );
    },
    [
      t,
      i18n,
      return_reasons,
      selectedToReturn,
      showUploadModal,
      validationErrors,
      updateReason,
      updateDetails,
      handleImagesSelected,
      handleRemoveImage,
      allow_exchanges,
      showExchangeModal,
      handleProductSelect,
      withdrawalMode,
      detailsMode,
      detailsMaxLength,
    ]
  );

  const renderItem = useCallback(
    (item: LineItem) => {
      // #144: a line is returnable only up to what Shopify has fulfilled. fulfilled 0 → not
      // returnable (block selection + message); fail-open to ordered qty when xConnector omits it.
      const { maxReturnable, notReturnable } = computeReturnable(
        item.quantity,
        item.fulfilled_quantity,
        item.returnable_quantity
      );
      const isGreyedOut = greyedOutItems.has(item.id) || notReturnable;
      const isSelected = isProductSelected(item.id);
      const selectedProduct = isSelected ? productSelected(item.id) : null;
      const retProds = selectedToReturn.filter(s => s.id === item.id);
      let selectedQuant = 0;
      if (selectedProduct) {
        selectedQuant = selectedProduct.showIndividual
          ? retProds.length
          : selectedProduct.quantity;
      }
      const cardClass = isGreyedOut
        ? 'xc-card xc-card--disabled'
        : isSelected
          ? 'xc-card'
          : 'xc-card xc-card--dim';
      const product = item.product_title;

      return (
        <div
          key={item.id}
          className={cardClass}
          data-line-item={item.id}
          aria-disabled={isGreyedOut || undefined}
        >
          <div className="xc-prod">
            {item.image_src ? (
              <div className="xc-prod__img">
                <img src={item.image_src} alt={product} />
              </div>
            ) : (
              <div className="xc-prod__img xc-prod__img--empty">
                <span className="xc-visually-hidden">
                  {t('products.noImage')}
                </span>
              </div>
            )}
            <div>
              <p className="xc-prod__name">{product}</p>
              <p className="xc-prod__meta">
                {item.variant_title}
                {item.variant_title ? ` ${'·'} ` : ''}
                <span className="xc-prod__price">
                  {formatPrice(item.total_price, item.currency)}
                </span>
              </p>
              {isGreyedOut && (
                <p className="xc-prod__note">
                  {greyedOutItems.has(item.id)
                    ? t('orderDetails.notAvailable')
                    : item.returnable_quantity === 0 &&
                        computeReturnable(
                          item.quantity,
                          item.fulfilled_quantity
                        ).maxReturnable > 0
                      ? t('orderDetails.alreadyInReturn')
                      : t('orderDetails.notFulfilled')}
                </p>
              )}
              {!isGreyedOut && !isSelected && (
                <div className="xc-prod__row">
                  <button
                    type="button"
                    id={`line-${item.id}-add`}
                    className="xc-btn xc-btn--sm"
                    aria-label={String(t('products.addFor', { product }))}
                    onClick={() => toggleProductSelection(item.id)}
                  >
                    {t('products.add')}
                  </button>
                </div>
              )}
              {isSelected && selectedProduct && (
                <div className="xc-prod__row">
                  <span
                    id={`qty-${item.id}-label`}
                    className="xc-visually-hidden"
                  >
                    {t('productDetails.quantity')}
                  </span>
                  <div className="xc-qty">
                    <button
                      type="button"
                      className="xc-qty__btn"
                      aria-label={String(t('products.qty.decrease'))}
                      onClick={() => updateQuantity(item.id, -1)}
                      disabled={selectedQuant <= 1}
                    >
                      {'−'}
                    </button>
                    <output
                      className="xc-qty__n"
                      aria-labelledby={`qty-${item.id}-label`}
                    >
                      {selectedQuant}
                    </output>
                    <button
                      type="button"
                      className="xc-qty__btn"
                      aria-label={String(t('products.qty.increase'))}
                      onClick={() => updateQuantity(item.id, 1)}
                      disabled={selectedQuant >= maxReturnable}
                    >
                      +
                    </button>
                  </div>
                  <span className="xc-chip xc-chip--ok">
                    {t('products.selected')}
                  </span>
                  <button
                    type="button"
                    id={`line-${item.id}-remove`}
                    className="xc-btn xc-btn--link"
                    aria-label={String(t('products.removeFor', { product }))}
                    onClick={() => toggleProductSelection(item.id)}
                  >
                    {t('products.remove')}
                  </button>
                  {selectedQuant > 1 && (
                    <button
                      type="button"
                      className="xc-btn xc-btn--link"
                      onClick={() => updateShowIndividual(item.id)}
                    >
                      {selectedProduct.showIndividual
                        ? t('productDetails.hideIndividualReasons')
                        : t('productDetails.individualReasons')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {isSelected &&
            selectedProduct &&
            retProds.map((p, index) =>
              renderToReturnItem(index, retProds.length, item, selectedQuant)
            )}
        </div>
      );
    },
    [
      greyedOutItems,
      t,
      return_reasons,
      selectedToReturn,
      showUploadModal,
      validationErrors,
      renderToReturnItem,
      isProductSelected,
      productSelected,
      updateQuantity,
      updateShowIndividual,
      toggleProductSelection,
    ]
  );

  // #107 (D4, D10): title row + order chip, merchant instruction lines,
  // the live counter, one card per line
  const orderName = order.order_name ? order.order_name : order.id;
  const chipText = `${t('orderDetails.order')} ${orderName}`;
  const returnInstruction = shopLabel(
    'dynamic:app_message.return_details_instruction',
    ''
  );
  const secondReturnWarning = shopLabel(
    'dynamic:app_message.second_return_warning',
    ''
  );
  const chooseInstruction = shopLabel(
    'dynamic:app_message.choose_product_instruction',
    ''
  );

  return (
    <>
      <div className="xc-screen xc-screen--wide">
        <Stepper currentStep={0} />
        <div className="xc-title-row">
          <h2 className="xc-title">{t('orderDetails.toReturn')}</h2>
          <span className="xc-chip" title={chipText}>
            {chipText}
          </span>
        </div>
        {returnInstruction && <p className="xc-intro">{returnInstruction}</p>}
        {chooseInstruction && <p className="xc-intro">{chooseInstruction}</p>}
        {/* #119: no line has shipped yet (null = unknown, never counted) */}
        {order.line_items.length > 0 &&
          order.line_items.every(item => item.fulfilled_quantity === 0) && (
            <div className="xc-notice xc-notice--warn" role="status">
              {t('orderDetails.orderNotFulfilled')}
            </div>
          )}
        {order_token.return_context?.has_prior_return &&
          secondReturnWarning.trim() && (
            <div className="xc-notice xc-notice--warn" role="status">
              {secondReturnWarning}
            </div>
          )}
        <p className="xc-list-count" aria-live="polite">
          {t('productWithCount', {
            number: selectedToReturn.length,
            count: order.line_items.length,
          })}
        </p>
        {order.line_items.map(item => renderItem(item))}
      </div>
      {/* Order Footer: #105 (S1, D7) action bar; #72 no Back on the QR
          product selection (no lookup form to go back to) */}
      <ActionBar
        hideBack={standaloneQr}
        onBack={() => {
          // #496 (review r2 #1): leaving the flow drops the preferences draft
          clearMethodsDrafts();
          return withdrawalMode && withdrawal_receipt
            ? // #149: back to the receipt, with the full continuation context
              navigate('/withdrawal/' + withdrawal_id, {
                state: {
                  receipt: withdrawal_receipt,
                  order: order_token.order_details,
                  proof,
                  shop,
                  standalone: standaloneQr,
                  ...(standaloneQr
                    ? standaloneNavigationState(handoff, account)
                    : {}),
                },
              })
            : navigate('/order-form-page?proof=' + proof + '&shop=' + shop);
        }}
        backLabel={t('buttons.back')}
        primaryLabel={t('buttons.continue')}
        onPrimary={handleContinueClick}
        primaryDisabled={isContinueDisabled}
        showChevron
      />
    </>
  );
};

export default OrderDetailsPage;
